import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext, SttPrivilegeList } from '../../../sporttia/all';
import { updateElementInArray } from '../../../lib/utils';

type Privilege = {
    id: string;
    name: string;
    description?: string;
    active: boolean;
    isDefaultOnSC?: boolean;
};

type Item = {
    id: string;
};

type Props = {
    item: Item;
};

// Privileges to hide from the list (not configurable in Sporttia Manager)
// event_signup_pay_later: This is a payment option inside events, not a privilege
const HIDDEN_PRIVILEGES = ['event_signup_pay_later'];

/**
 * Check if a privilege should be hidden based on its name/code
 * The privilege name contains the translation key like "hart.privilege.{code}.name"
 */
const isHiddenPrivilege = (privilege: Privilege): boolean => {
    return HIDDEN_PRIVILEGES.some(
        (hidden) => privilege.name.includes(hidden) || privilege.id === hidden,
    );
};

/**
 * Privileges for the user group
 * Group item - A user group as obtained in /group/Group.js
 */
export default function GroupPrivileges({ item }: Props): React.ReactElement {
    const [privileges, setPrivileges] = useState<Privilege[]>([]);

    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    // Load privilege list on 'item' change
    useEffect(() => {
        if (item) {
            loadPrivileges();
        }
    }, [item?.id]);

    // Load privileges from API
    function loadPrivileges() {
        cxt?.api('GET', `/groups/${item.id}/privileges`, {
            params: {
                includeInactive: 1,
            },
            success: (response: { rows?: Privilege[] }) => {
                if (response.rows) {
                    setPrivileges(response.rows);
                }
            },
        });
    }

    /**
     * Toggle privilege - POST to activate, DELETE to deactivate
     */
    const togglePrivilege = ({
        id,
        value,
    }: {
        id: string;
        value: boolean;
    }): void => {
        const privilege = privileges.find((p) => p.id === id);
        if (!privilege) return;

        const updatedPrivilege = { ...privilege, active: value };

        if (value) {
            cxt?.api('POST', `/groups/${item.id}/privileges/${id}`, {
                success: () => {
                    setPrivileges(
                        updateElementInArray(privileges, updatedPrivilege),
                    );
                },
            });
        } else {
            cxt?.api('DELETE', `/groups/${item.id}/privileges/${id}`, {
                success: () => {
                    setPrivileges(
                        updateElementInArray(privileges, updatedPrivilege),
                    );
                },
            });
        }
    };

    // Filter out hidden privileges before rendering
    const visiblePrivileges = privileges.filter(
        (privilege) => !isHiddenPrivilege(privilege),
    );

    return (
        <SttPrivilegeList
            privileges={visiblePrivileges}
            onToggle={togglePrivilege}
            title={t('Privilege')}
        />
    );
}
